import { useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'store';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { useGetPurchaseRequestGroupsQuery } from 'store/slices/clientV2/purchaseRequests';
import {
  PurchaseRequestGroupSchema,
  PurchaseRequestGroupSchemaLite,
  PurchaseRequestGroupType,
} from 'types/clientV2/purchaseRequests';
import { QueryFilterOperators, NullOperatorValues } from 'types/api';
import { setRecentCartId, setRecentRfqId } from 'store/slices/cartPreferences';
import { useGetUserQuery } from 'store/slices/apiV1/user';

export const useActivePurchaseRequestGroups = (
  type: PurchaseRequestGroupType = PurchaseRequestGroupType.CART
) => {
  const storedRecentCartId = useSelector(
    (state) => state.cartPreferences.recentCartId
  );
  const storedRecentRfqId = useSelector(
    (state) => state.cartPreferences.recentRfqId
  );
  const dispatch = useDispatch();
  const { data: user } = useGetUserQuery();

  const storedRecentId =
    type === PurchaseRequestGroupType.CART
      ? storedRecentCartId
      : storedRecentRfqId;

  const { data, isLoading } = useServerSideQuery<
    PurchaseRequestGroupSchema,
    Pick<
      PurchaseRequestGroupSchemaLite,
      'id' | 'name' | 'createdAt' | 'type' | 'numLines' | 'approvedAt'
    >
  >(useGetPurchaseRequestGroupsQuery, {
    pageSize: 100,
    schema: ['id', 'name', 'createdAt', 'type', 'numLines', 'approvedAt'],
    filters: [
      {
        field: 'requestedBy',
        operator: QueryFilterOperators['='],
        value: user?.id,
      },
      'or',
      {
        field: 'shared',
        operator: QueryFilterOperators['='],
        value: true,
      },
      'and',
      {
        field: 'archivedAt',
        operator: QueryFilterOperators.isEmpty,
        value: NullOperatorValues.isEmpty,
      },
      {
        field: 'type',
        operator: QueryFilterOperators['='],
        value: type,
      },
      {
        field: 'approvedTotal',
        operator: QueryFilterOperators.isEmpty,
        value: NullOperatorValues.isEmpty,
      },
      {
        field: 'approvedAt',
        operator: QueryFilterOperators.isEmpty,
        value: NullOperatorValues.isEmpty,
      },
    ],
    sort: [{ orderBy: 'createdAt', orderDirection: 'desc' }],
  });

  const activeGroups = useMemo(() => data?.data || [], [data?.data]);

  const mostRecentGroup = useMemo(() => {
    if (activeGroups.length === 0) {
      return null;
    }

    if (storedRecentId) {
      const storedGroup = activeGroups.find(
        (group) => group.id === storedRecentId
      );
      if (storedGroup) {
        return storedGroup;
      }
    }

    return activeGroups[0] || null;
  }, [activeGroups, storedRecentId]);

  const setRecentGroup = useCallback(
    (groupId: string | null) => {
      if (type === PurchaseRequestGroupType.CART) {
        dispatch(setRecentCartId(groupId));
      } else {
        dispatch(setRecentRfqId(groupId));
      }
    },
    [dispatch, type]
  );

  useEffect(() => {
    if (storedRecentId && !activeGroups.find((g) => g.id === storedRecentId)) {
      setRecentGroup(null);
    }
  }, [activeGroups, storedRecentId, setRecentGroup]);

  return {
    activeGroups,
    mostRecentGroup,
    setRecentGroup,
    isLoading,
    hasActiveGroups: activeGroups.length > 0,
  };
};

export const useActiveCarts = () => {
  const result = useActivePurchaseRequestGroups(PurchaseRequestGroupType.CART);
  return {
    activeCarts: result.activeGroups,
    mostRecentCart: result.mostRecentGroup,
    setRecentCart: result.setRecentGroup,
    isLoading: result.isLoading,
    hasActiveCarts: result.hasActiveGroups,
  };
};

export const useActiveRfqs = () => {
  const result = useActivePurchaseRequestGroups(PurchaseRequestGroupType.RFQ);
  return {
    activeRfqs: result.activeGroups,
    mostRecentRfq: result.mostRecentGroup,
    setRecentRfq: result.setRecentGroup,
    isLoading: result.isLoading,
    hasActiveRfqs: result.hasActiveGroups,
  };
};
